import { useState } from 'react';
import { Card, CardContent, Button, Badge } from '@/components/ui';
import { metricGroupApi } from '@/services/metric-group-api';
import type {
  IHomepageMetricGroup,
  IHomepageMetricGroupUpdate,
} from '@skystone/shared';
import { MetricGroupFormFields } from './MetricGroupFormFields';
import {
  toMetricGroupFormState,
  validateMetricGroupLinkTarget,
  validateMetricGroupTitle,
  type MetricGroupFormState,
} from './metric-group-form-helpers';

interface MetricGroupRowEditorProps {
  group: IHomepageMetricGroup;
  onUpdated: (group: IHomepageMetricGroup) => void;
}

export function MetricGroupRowEditor({
  group,
  onUpdated,
}: Readonly<MetricGroupRowEditorProps>) {
  const [form, setForm] = useState<MetricGroupFormState>(() => toMetricGroupFormState(group));
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const linkTargetError = validateMetricGroupLinkTarget(form.linkTarget);
  const titleError = validateMetricGroupTitle(form.title);
  const isValid = linkTargetError === null && titleError === null;

  function setField<K extends keyof MetricGroupFormState>(key: K, value: MetricGroupFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setServerError(null);
    setSavedFlash(false);
    try {
      const body: IHomepageMetricGroupUpdate = { ...form };
      const updated = await metricGroupApi.update(group.groupKey, body);
      onUpdated(updated);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const idleSaveLabel = savedFlash ? 'Saved!' : 'Save';

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-foreground">{group.title}</h2>
            <Badge variant="secondary">{group.groupKey}</Badge>
          </div>
          {group.updatedBy && (
            <p className="text-xs text-muted-foreground">
              Last edited by {group.updatedBy}
            </p>
          )}
        </header>

        {serverError && (
          <p className="text-xs text-error" role="alert">{serverError}</p>
        )}

        <MetricGroupFormFields
          form={form}
          setField={setField}
          titleError={titleError}
          linkTargetError={linkTargetError}
          trailing={
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !isValid}
            >
              {saving ? 'Saving...' : idleSaveLabel}
            </Button>
          }
        />
      </CardContent>
    </Card>
  );
}
